/**
 * v4 Files - central document hub with v1 feature parity:
 *   • Snap-receipt CTA (with AI badge)
 *   • "Files your accountant requested" tracker
 *   • Search across filename + category
 *   • Category chips + Year filter
 *   • Grouped file list by FY
 *   • Preview modal with download/open actions
 *   • Per-file "Ask about this" → focused AI chat
 *   • Add-file sheet (camera / email / WhatsApp)
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search, Download, MessageCircleQuestion, FileText, Mail, Receipt as ReceiptIcon,
  Landmark, BarChart3, Plus, X, ChevronRight, Camera, MessageCircle as WhatsAppIcon,
  Sparkles, Upload, AlertCircle, CheckCircle2, Eye, Mic, MessageSquareText, Send, Loader2, Share2,
  HardDrive, Clock, Hash, Calendar, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';
import { listFiles, uploadFile, getFileDownloadUrl, getBusiness, sendMessage as sendClientMessage, useClient, ClientFile, ClientBusiness } from '@/services/api/client-auth';
import { toast } from 'sonner';
import { VoiceRecorder } from './VoiceRecorder';

const INSIGHT_COLORS = ['#35AD88', '#6366F1', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6', '#10B981', '#F43F5E', '#84CC16', '#0EA5E9'];

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

function formatRelDate(s?: string | null): string {
  if (!s) return '-';
  const d = new Date(s);
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

type Category = 'tax' | 'bas' | 'financials' | 'letters' | 'receipts' | 'other';
type FilterKey = Category | 'all' | 'sent';

interface FileRow {
  id: string; filename: string; category: Category;
  fy: string; date: string; pages: number; sizeKb: number;
  preparedBy?: string; important?: boolean;
  source?: string;     // 'client_upload' if user sent it, else from firm/integration
  rawDate?: Date;      // for chronological sort in the "Sent" view
  contentType?: string; // drives the preview renderer (image / pdf / fallback)
}

const FILES: FileRow[] = [
  { id: 'f1',  filename: 'BAS Q3 2025-26 (draft).pdf',         category: 'bas',         fy: '2025-26', date: '12 May 2026', pages: 6,  sizeKb: 124, preparedBy: 'Marcus Bell', important: true },
  { id: 'f2',  filename: 'Balance Sheet - 30 Apr 2026.pdf',    category: 'financials',  fy: '2025-26', date: '5 May 2026',  pages: 4,  sizeKb: 89,  preparedBy: 'Marcus Bell' },
  { id: 'f3',  filename: 'P&L YTD - Apr 2026.pdf',             category: 'financials',  fy: '2025-26', date: '5 May 2026',  pages: 8,  sizeKb: 142, preparedBy: 'Marcus Bell' },
  { id: 'f4',  filename: 'BAS Q2 2025-26 - lodged.pdf',        category: 'bas',         fy: '2025-26', date: '28 Feb 2026', pages: 6,  sizeKb: 118, preparedBy: 'Marcus Bell' },
  { id: 'f5',  filename: 'BAS Q1 2025-26 - lodged.pdf',        category: 'bas',         fy: '2025-26', date: '28 Nov 2025', pages: 6,  sizeKb: 116, preparedBy: 'Marcus Bell' },
  { id: 'f6',  filename: 'ATO - BAS reminder.pdf',             category: 'letters',     fy: '2025-26', date: '12 May 2026', pages: 1,  sizeKb: 38 },
  { id: 'f7',  filename: 'ASIC - Annual review.pdf',           category: 'letters',     fy: '2025-26', date: '3 May 2026',  pages: 2,  sizeKb: 64 },
  { id: 'f8',  filename: 'Bunnings receipt - 234.50.pdf',      category: 'receipts',    fy: '2025-26', date: '15 May 2026', pages: 1,  sizeKb: 28 },
  { id: 'f9',  filename: 'Smith Electrical invoice - 1,240.pdf', category: 'receipts',  fy: '2025-26', date: '15 May 2026', pages: 1,  sizeKb: 34 },
  { id: 'f10', filename: 'Tax Return FY2024-25.pdf',           category: 'tax',         fy: '2024-25', date: '12 Oct 2025', pages: 12, sizeKb: 230, preparedBy: 'Marcus Bell', important: true },
  { id: 'f11', filename: 'Financial Statements FY2024-25.pdf', category: 'financials',  fy: '2024-25', date: '12 Oct 2025', pages: 14, sizeKb: 248, preparedBy: 'Marcus Bell' },
  { id: 'f12', filename: 'Balance Sheet - 30 Jun 2025.pdf',    category: 'financials',  fy: '2024-25', date: '8 Oct 2025',  pages: 4,  sizeKb: 92,  preparedBy: 'Marcus Bell' },
  { id: 'f13', filename: 'Notice of Assessment 2024-25.pdf',   category: 'tax',         fy: '2024-25', date: '20 Oct 2025', pages: 2,  sizeKb: 56 },
  { id: 'f14', filename: 'BAS Q4 2024-25 - lodged.pdf',        category: 'bas',         fy: '2024-25', date: '28 Aug 2025', pages: 6,  sizeKb: 122, preparedBy: 'Marcus Bell' },
  { id: 'f15', filename: 'BAS Q3 2024-25 - lodged.pdf',        category: 'bas',         fy: '2024-25', date: '28 May 2025', pages: 6,  sizeKb: 118, preparedBy: 'Marcus Bell' },
  { id: 'f16', filename: 'Tax Return FY2023-24.pdf',           category: 'tax',         fy: '2023-24', date: '14 Sep 2024', pages: 12, sizeKb: 224, preparedBy: 'Marcus Bell' },
  { id: 'f17', filename: 'Financial Statements FY2023-24.pdf', category: 'financials',  fy: '2023-24', date: '14 Sep 2024', pages: 14, sizeKb: 242, preparedBy: 'Marcus Bell' },
  { id: 'f18', filename: 'Trust Deed.pdf',                     category: 'other',       fy: '2023-24', date: '5 Jul 2023',  pages: 28, sizeKb: 412 },
  { id: 'f19', filename: 'Tax Return FY2022-23.pdf',           category: 'tax',         fy: '2022-23', date: '20 Aug 2023', pages: 11, sizeKb: 218, preparedBy: 'Marcus Bell' },
  { id: 'f20', filename: 'Insurance - Public Liability.pdf',   category: 'other',       fy: '2022-23', date: '1 Jul 2022',  pages: 4,  sizeKb: 86 },
];

const CATEGORIES: { key: FilterKey; tKey: string }[] = [
  { key: 'all',        tKey: 'category_all' },
  { key: 'sent',       tKey: 'category_sent' },
  { key: 'tax',        tKey: 'category_tax' },
  { key: 'bas',        tKey: 'category_bas' },
  { key: 'financials', tKey: 'category_financials' },
  { key: 'letters',    tKey: 'category_letters' },
  { key: 'receipts',   tKey: 'category_receipts' },
  { key: 'other',      tKey: 'category_other' },
];

/**
 * File-type badge (PDF, XLS, IMG, etc.) shown in place of the category
 * icon on each row + preview header. Identifies file format at a glance
 * - same colour conventions native file managers use, so the user
 * doesn't have to read the extension off the filename. Extend as we add
 * support for more types.
 */
function getFileTypeStyle(filename: string, contentType?: string): {
  label: string; bg: string; text: string; ring: string;
} {
  const ct  = (contentType || '').toLowerCase();
  const ext = (filename.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
  if (ct === 'application/pdf' || ext === 'pdf') {
    return { label: 'PDF', bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-700 dark:text-rose-300', ring: 'ring-rose-200 dark:ring-rose-900/60' };
  }
  if (ct.includes('spreadsheetml') || ct.includes('vnd.ms-excel') || ['xlsx','xls','csv'].includes(ext)) {
    return { label: ext === 'csv' ? 'CSV' : 'XLS', bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-200 dark:ring-emerald-900/60' };
  }
  if (ct.includes('wordprocessingml') || ct.includes('msword') || ['docx','doc'].includes(ext)) {
    return { label: 'DOC', bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-300', ring: 'ring-blue-200 dark:ring-blue-900/60' };
  }
  if (ct.startsWith('image/') || ['png','jpg','jpeg','gif','webp','heic','heif','svg'].includes(ext)) {
    return { label: 'IMG', bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', ring: 'ring-teal-200 dark:ring-teal-900/60' };
  }
  if (ct === 'application/json' || ext === 'json') {
    return { label: 'JSON', bg: 'bg-purple-50 dark:bg-purple-950/40', text: 'text-purple-700 dark:text-purple-300', ring: 'ring-purple-200 dark:ring-purple-900/60' };
  }
  if (ct.startsWith('audio/') || ['mp3','wav','m4a','ogg','flac'].includes(ext)) {
    return { label: 'AUD', bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-200 dark:ring-amber-900/60' };
  }
  if (ct.startsWith('video/') || ['mp4','mov','webm','mkv'].includes(ext)) {
    return { label: 'VID', bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-700 dark:text-indigo-300', ring: 'ring-indigo-200 dark:ring-indigo-900/60' };
  }
  return { label: (ext.slice(0, 4) || 'FILE').toUpperCase(), bg: 'bg-muted', text: 'text-foreground/80', ring: 'ring-border' };
}

const CAT_STYLE: Record<Category, { icon: any; bg: string; text: string; ring: string }> = {
  tax:        { icon: Landmark,    bg: 'bg-rose-50 dark:bg-rose-950/40',       text: 'text-rose-700 dark:text-rose-300',       ring: 'ring-rose-200 dark:ring-rose-900/60' },
  bas:        { icon: FileText,    bg: 'bg-amber-50 dark:bg-amber-950/40',     text: 'text-amber-700 dark:text-amber-300',     ring: 'ring-amber-200 dark:ring-amber-900/60' },
  financials: { icon: BarChart3,   bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-200 dark:ring-emerald-900/60' },
  letters:    { icon: Mail,        bg: 'bg-[var(--v4-accent-tint)]',           text: 'text-[var(--v4-accent-text)]',           ring: 'ring-[var(--v4-accent)]/30' },
  receipts:   { icon: ReceiptIcon, bg: 'bg-teal-50 dark:bg-teal-950/40',       text: 'text-teal-700 dark:text-teal-300',       ring: 'ring-teal-200 dark:ring-teal-900/60' },
  other:      { icon: FileText,    bg: 'bg-muted',                             text: 'text-foreground/80',                     ring: 'ring-border' },
};

interface RequestedFile {
  id: string; title: string; description: string; dueDate: string; status: 'pending' | 'uploaded';
}

const INITIAL_REQUESTS: RequestedFile[] = [];

function serverFileToRow(f: ClientFile): FileRow {
  const d = new Date(f.createdAt);
  const fy = f.financialYear || (() => {
    const y = d.getFullYear();
    const m = d.getMonth();
    return m >= 6 ? `${y}-${(y + 1) % 100}` : `${y - 1}-${y % 100}`;
  })();
  const cat = (['tax', 'bas', 'financials', 'letters', 'receipts', 'other'].includes(f.category) ? f.category : 'other') as Category;
  return {
    id: f.id,
    filename: f.filename,
    category: cat,
    fy,
    date: d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
    pages: 1,
    sizeKb: Math.round(f.fileSizeBytes / 1024),
    preparedBy: f.source === 'client_upload' ? undefined : 'Your accountant',
    source: f.source,
    rawDate: d,
    contentType: f.contentType,
  };
}

export default function FilesPageV4() {
  const { t, dir } = useV4();
  const qc = useQueryClient();
  const loggedInClient = useClient();
  const firmName = loggedInClient?.tenantName || 'Your accountant';
  const [category, setCategory] = useState<FilterKey>('all');
  const [year, setYear] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [preview, setPreview] = useState<FileRow | null>(null);
  const [requests, setRequests] = useState<RequestedFile[]>(INITIAL_REQUESTS);
  const [uploading, setUploading] = useState<{ filename: string; pct: number } | null>(null);
  const [sendMode, setSendMode] = useState<'idle' | 'voice' | 'message'>('idle');
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  async function handleSendMessage() {
    const text = messageText.trim();
    if (!text || sendingMessage) return;
    setSendingMessage(true);
    try {
      await sendClientMessage(text);
      toast.success('Sent to your accountant');
      qc.invalidateQueries({ queryKey: ['client-messages'] });
      qc.invalidateQueries({ queryKey: ['client-timeline'] });
      setMessageText('');
      setSendMode('idle');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to send');
    } finally {
      setSendingMessage(false);
    }
  }

  const { data: serverFiles = [], isLoading } = useQuery({
    queryKey: ['client-files'],
    queryFn: () => listFiles(),
    staleTime: 30_000,
  });

  // Document KPIs + charts use the same /me/business aggregate the home/money
  // pages already cache, so this is a free piggyback in 99% of cases.
  const { data: business } = useQuery<ClientBusiness>({
    queryKey: ['client-business'],
    queryFn: getBusiness,
    staleTime: 60_000,
  });
  const [insightsOpen, setInsightsOpen] = useState(false);

  // Client app v1: only surface PDFs to the user. We'll add xlsx / csv /
  // json / images later - for now their viewers and chat handlers aren't
  // mature enough that the file does anything useful in the app.
  const ROWS: FileRow[] = useMemo(() => {
    return serverFiles
      .filter(f =>
        (f.contentType || '').toLowerCase() === 'application/pdf' ||
        /\.pdf$/i.test(f.filename || '')
      )
      .map(serverFileToRow);
  }, [serverFiles]);

  async function handleUpload(file: File, note?: string) {
    if (!file) return;
    setUploading({ filename: file.name, pct: 0 });
    try {
      await uploadFile(file, {
        note,
        onProgress: pct => setUploading(prev => prev ? { ...prev, pct } : prev),
      });
      qc.invalidateQueries({ queryKey: ['client-files'] });
      qc.invalidateQueries({ queryKey: ['client-timeline'] });
      qc.invalidateQueries({ queryKey: ['client-business'] });
      toast.success(`${file.name} sent to your accountant`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  }

  async function handlePreviewShare(row: FileRow) {
    let toastId: string | number | undefined;
    try {
      const { url, filename, contentType } = await getFileDownloadUrl(row.id);
      const shareName = filename || row.filename;
      const nav = typeof navigator !== 'undefined' ? (navigator as any) : null;

      // Best path: download the actual bytes and hand them to the OS share
      // sheet as a File. WhatsApp/email/Messages/Drive then attach the
      // physical file, not a link. S3 CORS already allows our origin so
      // fetch() works.
      if (nav?.share && nav.canShare) {
        toastId = toast.loading('Preparing file…');
        try {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const file = new File(
            [blob],
            shareName,
            { type: contentType || blob.type || 'application/octet-stream' }
          );
          if (nav.canShare({ files: [file] })) {
            toast.dismiss(toastId);
            await nav.share({ files: [file], title: shareName, text: shareName });
            return;
          }
        } catch (e: any) {
          // CORS, network, or canShare returned false - fall through to
          // the URL-share path below.
          if (toastId) toast.dismiss(toastId);
          toastId = undefined;
          if (e?.name === 'AbortError') return; // user cancelled
        }
      }

      // Fallback 1 - share the link instead of the file.
      if (nav?.share) {
        try {
          await nav.share({ title: shareName, text: shareName, url });
          return;
        } catch (e: any) {
          if (e?.name === 'AbortError') return;
        }
      }

      // Fallback 2 - desktop without share sheet. Copy the link.
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      if (toastId) toast.dismiss(toastId);
      toast.error(e?.response?.data?.error || 'Could not share file');
    }
  }

  async function handlePreviewDownload(row: FileRow) {
    try {
      const { url } = await getFileDownloadUrl(row.id);
      // Open in a new tab WITHOUT the `download` attribute so PDFs/images
      // render inline in the browser instead of forcing a save dialog.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not open file');
    }
  }

  const years = useMemo(() => Array.from(new Set(ROWS.map(f => f.fy))).sort().reverse(), [ROWS]);

  const filtered = useMemo(() => ROWS.filter(f => {
    if (category === 'sent') {
      if (f.source !== 'client_upload') return false;
    } else if (category !== 'all') {
      if (f.category !== category) return false;
    }
    if (year !== 'all' && f.fy !== year) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!f.filename.toLowerCase().includes(q) && !f.category.includes(q)) return false;
    }
    return true;
  }), [ROWS, category, year, search]);

  const grouped = useMemo(() => {
    const out: Record<string, FileRow[]> = {};
    if (category === 'sent') {
      // Group by month for the "Sent" view - recent uploads are time-ordered, not FY.
      const sorted = [...filtered].sort((a, b) => {
        const at = a.rawDate?.getTime() || 0;
        const bt = b.rawDate?.getTime() || 0;
        return bt - at;
      });
      for (const f of sorted) {
        const monthKey = f.rawDate
          ? f.rawDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
          : 'Earlier';
        (out[monthKey] = out[monthKey] || []).push(f);
      }
    } else {
      for (const f of filtered) (out[f.fy] = out[f.fy] || []).push(f);
    }
    return out;
  }, [filtered, category]);

  const markUploaded = (id: string) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'uploaded', dueDate: t('uploaded_just_now') } : r));
  };
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-3 pb-4">
      <div className="pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('files_title')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('files_subtitle')}</p>
      </div>

      {/* Send-to-firm command hub - the central communication channel */}
      <div className="relative overflow-hidden rounded-lg border border-[var(--v4-accent)]/30 bg-[var(--v4-accent-tint)] p-5">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[var(--v4-accent)]/15 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              <Sparkles className="h-2.5 w-2.5" /> AI
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--v4-accent-text)]">
              Direct line to {t('firm_initials')}
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground leading-tight">{t('snap_receipt')}</h2>
          <p className="mt-1 text-xs text-foreground/80 leading-relaxed">{t('snap_hint')}</p>

          {/* Hidden inputs for upload/camera */}
          <input ref={fileInputRef} type="file"
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
            className="hidden" />
          <input ref={cameraInputRef} type="file"
            accept="image/*" capture="environment"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
            className="hidden" />

          {/* 4 send-action buttons - each one does the action directly, no sub-sheet */}
          {sendMode === 'idle' && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SendAction onClick={() => cameraInputRef.current?.click()} icon={Camera}            label={t('opt_camera')}  />
              <SendAction onClick={() => fileInputRef.current?.click()}   icon={Upload}            label={t('opt_upload')}  />
              <SendAction onClick={() => setSendMode('voice')}            icon={Mic}               label={t('opt_voice')}   />
              <SendAction onClick={() => setSendMode('message')}          icon={MessageSquareText} label={t('opt_message')} />
            </div>
          )}

          {/* Voice recorder - inline */}
          {sendMode === 'voice' && (
            <div className="mt-4">
              <VoiceRecorder onSent={() => setSendMode('idle')} onCancel={() => setSendMode('idle')} />
            </div>
          )}

          {/* Inline message composer */}
          {sendMode === 'message' && (
            <div className="mt-4 rounded-md border border-border bg-card p-3">
              <textarea
                autoFocus
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder={t('type_your_message')}
                rows={3}
                className="w-full bg-transparent text-sm placeholder:text-muted-foreground/60 focus:outline-none resize-none"
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => { setSendMode('idle'); setMessageText(''); }}
                  className="flex-1 rounded-md border border-border bg-card py-2 text-sm font-semibold text-foreground/80 hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sendingMessage}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-[var(--v4-accent)] py-2 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)] disabled:opacity-50"
                >
                  {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t('send_now')}
                </button>
              </div>
            </div>
          )}

          {/* Upload progress */}
          {uploading && (
            <div className="mt-3 rounded-md border border-[var(--v4-accent)]/40 bg-card p-2.5 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-[var(--v4-accent-text)]" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{uploading.filename}</p>
                <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-[var(--v4-accent)] transition-all" style={{ width: `${uploading.pct}%` }} />
                </div>
              </div>
              <span className="text-[10px] font-bold text-muted-foreground">{uploading.pct}%</span>
            </div>
          )}

          {/* Why use this */}
          <div className="mt-4 flex items-start gap-2 rounded-md border border-[var(--v4-accent)]/20 bg-card/60 p-2.5">
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[var(--v4-accent-text)] mt-0.5" />
            <div className="text-[11px] leading-snug">
              <p className="font-semibold text-foreground">{t('send_response_time', { firm: firmName })}</p>
              <p className="mt-0.5 text-muted-foreground">{t('send_tagline')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Requested files */}
      {requests.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/30 p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 dark:bg-amber-600 text-white shadow-sm">
              <Upload className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{t('requested_files')}</p>
              {pendingCount > 0 && <p className="text-[11px] text-amber-700 dark:text-amber-300">{pendingCount} {t('pending')}</p>}
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {requests.map(r => (
              <div key={r.id} className={cn('flex items-center gap-3 rounded-md bg-card p-3 border border-amber-100 dark:border-amber-900/40', r.status === 'uploaded' && 'opacity-75')}>
                <div className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                  r.status === 'uploaded' ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300')}>
                  {r.status === 'uploaded' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{r.title}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{r.description}</p>
                  <p className={cn('mt-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    r.status === 'uploaded' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300')}>
                    {r.dueDate}
                  </p>
                </div>
                {r.status === 'pending' && (
                  <button
                    onClick={() => markUploaded(r.id)}
                    className="flex-shrink-0 rounded-md bg-[var(--v4-accent)] px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-[var(--v4-accent-hover)]"
                  >
                    {t('upload')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document insights - KPIs + charts (collapsible) */}
      {business && business.totalDocuments > 0 && (
        <section className="rounded-lg border bg-card">
          <button
            onClick={() => setInsightsOpen(o => !o)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[var(--v4-accent-text)]" />
              <span className="text-sm font-semibold">Your documents at a glance</span>
            </div>
            {insightsOpen
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>

          {insightsOpen && (
            <div className="border-t px-4 pt-3 pb-4 space-y-4">
              {/* KPIs */}
              <div className="grid grid-cols-2 gap-2.5">
                <InsightKpi icon={FileText} label="Total documents" value={business.totalDocuments.toLocaleString()} sub={`${business.indexedDocuments.toLocaleString()} indexed`} />
                <InsightKpi icon={HardDrive} label="Total size" value={formatBytes(business.totalSizeBytes)} sub={business.totalSizeBytes ? 'across all files' : 'no files yet'} />
                <InsightKpi icon={Clock} label="Last upload" value={formatRelDate(business.lastDocumentAt)} sub={business.lastDocumentAt ? new Date(business.lastDocumentAt).toLocaleString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '-'} />
                <InsightKpi icon={Hash} label="Categories" value={business.categoryBreakdown.length.toString()} sub={business.yearBreakdown.length > 0 ? `${business.yearBreakdown.length} financial years` : 'no breakdown yet'} />
              </div>

              {/* Categories chart */}
              {business.categoryBreakdown.length > 0 && (
                <div className="rounded-md border bg-background p-3">
                  <p className="text-xs font-semibold mb-2">Documents by category</p>
                  <div className="h-44 -mx-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={business.categoryBreakdown.map(c => ({ name: titleCase(c.category), count: c.count }))} layout="vertical" margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
                        <XAxis type="number" hide />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: 'var(--muted)' }} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11 }} />
                        <Bar dataKey="count" radius={[0, 5, 5, 0]}>
                          {business.categoryBreakdown.map((_, i) => (
                            <Cell key={i} fill={INSIGHT_COLORS[i % INSIGHT_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Per-FY chart */}
              {business.yearBreakdown.length > 0 && (
                <div className="rounded-md border bg-background p-3">
                  <p className="text-xs font-semibold mb-2">Documents per financial year</p>
                  <div className="h-36 -mx-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={business.yearBreakdown.map(y => ({ year: `FY${y.year}`, count: y.count }))} margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
                        <XAxis dataKey="year" tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip cursor={{ fill: 'var(--muted)' }} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11 }} />
                        <Bar dataKey="count" fill="var(--v4-accent)" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Key dates */}
              {business.keyDates?.length > 0 && (
                <div className="rounded-md border bg-background p-3">
                  <p className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> Key dates
                  </p>
                  <ul className="space-y-2">
                    {business.keyDates.slice(0, 5).map((kd, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs">
                        <span className="text-[var(--v4-accent-text)] font-semibold whitespace-nowrap uppercase tracking-wider pt-0.5 min-w-[64px]">
                          {kd.date ? new Date(kd.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '-'}
                        </span>
                        <span className="text-foreground">{kd.label || kd.name || '-'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Search */}
      <div className="relative">
        <Search className={cn('absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60', dir === 'rtl' ? 'right-3' : 'left-3')} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('search_files')}
          className={cn(
            'w-full rounded-md border border-border bg-card py-2.5 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none',
            dir === 'rtl' ? 'pr-9 pl-3' : 'pl-9 pr-3'
          )}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className={cn('absolute top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground/80', dir === 'rtl' ? 'left-3' : 'right-3')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Category - underline tab bar + Year dropdown on the right */}
      <div className="flex items-center justify-between gap-2 border-b border-border">
        <nav className="-mb-px flex flex-1 gap-4 overflow-x-auto">
          {CATEGORIES.map(c => {
            const count =
              c.key === 'all'  ? ROWS.length :
              c.key === 'sent' ? ROWS.filter(f => f.source === 'client_upload').length :
              ROWS.filter(f => f.category === c.key).length;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key as any)}
                className={cn(
                  'flex-shrink-0 border-b-2 px-0.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors',
                  active
                    ? 'border-[var(--v4-accent)] text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground/80'
                )}
              >
                {t(c.tKey)}
                <span className={cn(
                  'ml-1.5 rounded-md px-1.5 py-0.5 text-[10px] tabular-nums',
                  active ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
        <select
          value={year}
          onChange={e => setYear(e.target.value)}
          className="flex-shrink-0 -mb-px rounded-md border border-border bg-card px-2 py-1.5 text-xs font-semibold text-foreground/80 focus:border-foreground focus:outline-none"
        >
          <option value="all">{t('all_years')}</option>
          {years.map(y => <option key={y} value={y}>FY {y}</option>)}
        </select>
      </div>

      {/* Files grouped */}
      {Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">{t('no_files_match')}</p>
        </div>
      ) : (
        (category === 'sent'
          ? Object.entries(grouped)  // already ordered by inserted month, newest first
          : Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]))
        ).map(([groupKey, rows]) => (
          <div key={groupKey}>
            <p className="px-1 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/60">
              {category === 'sent' ? groupKey : `FY ${groupKey}`}
            </p>
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <ul className="divide-y divide-border/60">
                {rows.map(f => <FileRowItem key={f.id} file={f} t={t} dir={dir} onPreview={() => setPreview(f)} />)}
              </ul>
            </div>
          </div>
        ))
      )}

      {showAdd && <AddFileSheet onClose={() => setShowAdd(false)} t={t} />}
      {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} onDownload={() => handlePreviewDownload(preview)} onShare={() => handlePreviewShare(preview)} t={t} />}
    </div>
  );
}

function FileRowItem({ file, t, dir, onPreview }: {
  file: FileRow; t: (k: string, vars?: any) => string; dir: 'ltr' | 'rtl'; onPreview: () => void;
}) {
  const ft = getFileTypeStyle(file.filename, file.contentType);

  return (
    <li>
      <button
        onClick={onPreview}
        className="group flex w-full items-start gap-3 px-4 py-3 text-start hover:bg-muted/40 transition-colors"
      >
        <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ring-1 text-[10px] font-bold tracking-wide', ft.bg, ft.text, ft.ring)}>
          {ft.label}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
            <span>{file.date}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{t('pages_count', { n: file.pages })}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{file.sizeKb} KB</span>
            {file.important && (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0 text-[9px] font-bold text-amber-700 dark:text-amber-300">DRAFT</span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Link
              to={`/client/files/${file.id}/chat`}
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
            >
              <MessageCircleQuestion className="h-3 w-3" />
              {t('ask_about_this')}
            </Link>
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
              <Download className="h-3 w-3" />
              {t('download')}
            </span>
          </div>
        </div>
        <ChevronRight className={cn('h-4 w-4 flex-shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground', dir === 'rtl' && 'rotate-180')} />
      </button>
    </li>
  );
}

function PreviewModal({ file, onClose, onDownload, onShare, t }: { file: FileRow; onClose: () => void; onDownload: () => void; onShare: () => void; t: (k: string, vars?: any) => string }) {
  const ft = getFileTypeStyle(file.filename, file.contentType);
  const [url, setUrl] = useState<string | null>(null);
  const [resolvedType, setResolvedType] = useState<string>(file.contentType || '');
  const [loadingUrl, setLoadingUrl] = useState(true);
  const [renderError, setRenderError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingUrl(true);
    setRenderError(false);
    getFileDownloadUrl(file.id)
      .then(({ url: u, contentType: ct }) => {
        if (cancelled) return;
        setUrl(u);
        if (ct) setResolvedType(ct);
      })
      .catch(() => { if (!cancelled) setRenderError(true); })
      .finally(() => { if (!cancelled) setLoadingUrl(false); });
    return () => { cancelled = true; };
  }, [file.id]);

  const mime = (resolvedType || guessMimeFromName(file.filename)).toLowerCase();
  const kind: 'image' | 'pdf' | 'audio' | 'video' | 'other' =
    mime.startsWith('image/')  ? 'image' :
    mime.includes('pdf')       ? 'pdf'   :
    mime.startsWith('audio/')  ? 'audio' :
    mime.startsWith('video/')  ? 'video' :
    'other';

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/50 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 top-10 z-50 mx-auto max-w-[720px] overflow-hidden rounded-t-3xl bg-background shadow-2xl animate-in slide-in-from-bottom duration-300 flex flex-col">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ring-1 text-[10px] font-bold tracking-wide', ft.bg, ft.text, ft.ring)}>
              {ft.label}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{file.filename}</p>
              <p className="text-[11px] text-muted-foreground">FY {file.fy} · {file.date} · {file.sizeKb} KB</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Viewer area */}
        <div className="flex flex-1 items-center justify-center overflow-hidden bg-muted">
          {loadingUrl ? (
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-xs">Loading…</p>
            </div>
          ) : !url || renderError ? (
            <PreviewFallback file={file} t={t} onDownload={onDownload} />
          ) : kind === 'image' ? (
            <img
              src={url}
              alt={file.filename}
              className="max-h-full max-w-full object-contain"
              onError={() => setRenderError(true)}
            />
          ) : kind === 'pdf' ? (
            <PdfPreview url={url} file={file} t={t} onDownload={onDownload} />
          ) : kind === 'audio' ? (
            <div className="w-full max-w-md px-4">
              <audio src={url} controls className="w-full" />
            </div>
          ) : kind === 'video' ? (
            <video src={url} controls className="max-h-full max-w-full" />
          ) : (
            <PreviewFallback file={file} t={t} onDownload={onDownload} />
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-border bg-card p-3">
          <Link
            to={`/client/files/${file.id}/chat`}
            onClick={onClose}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[var(--v4-accent)] px-4 py-3 text-sm font-semibold text-white whitespace-nowrap hover:bg-[var(--v4-accent-hover)]"
          >
            <MessageCircleQuestion className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{t('ask_about_this')}</span>
          </Link>
          <button
            onClick={onDownload}
            className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-lg border border-border bg-card text-foreground/80 hover:border-muted-foreground/40 active:scale-95 transition-transform"
            aria-label={t('download')}
            title={t('download')}
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={onShare}
            className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-lg border border-border bg-card text-foreground/80 hover:border-muted-foreground/40 active:scale-95 transition-transform"
            aria-label={t('share')}
            title={t('share')}
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * PdfPreview - cross-platform PDF rendering.
 *
 * Desktop browsers render PDFs inline via <iframe> just fine. Mobile browsers
 * (Android Chrome especially, plus most in-app webviews) refuse to render
 * PDFs inside an iframe and just show a blank gray rectangle.
 *
 * Strategy:
 *   1. Detect mobile UA. On mobile, render a prominent "Open PDF" card that
 *      uses window.open() - this lets the OS hand the URL to the native PDF
 *      viewer (Files, Drive, Safari, Chrome's built-in viewer), which works
 *      reliably everywhere.
 *   2. On desktop, render the iframe directly. Browsers know how to handle it.
 *   3. Provide an explicit fallback button regardless, so the user is never
 *      stuck with a blank screen.
 */
function PdfPreview({ url, file, t, onDownload }: { url: string; file: FileRow; t: (k: string) => string; onDownload: () => void }) {
  const isMobile = typeof navigator !== 'undefined' &&
    /Mobi|Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  if (isMobile) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-card shadow-sm border border-border">
          <FileText className="h-10 w-10 text-rose-500" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{file.filename}</p>
          <p className="mt-1 text-xs text-muted-foreground">PDF · {file.sizeKb} KB</p>
        </div>
        <button
          onClick={onDownload}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--v4-accent)] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[var(--v4-accent-hover)] active:scale-95 transition-transform"
        >
          <Eye className="h-4 w-4" />
          Open PDF
        </button>
        <p className="text-[11px] text-muted-foreground max-w-xs">
          Tap to view the PDF in your browser. It will open in a new tab.
        </p>
      </div>
    );
  }

  // Desktop - iframe renders the PDF using the browser's built-in viewer.
  return (
    <iframe
      src={url}
      title={file.filename}
      className="h-full w-full border-0 bg-white"
    />
  );
}

function PreviewFallback({ file, t, onDownload }: { file: FileRow; t: (k: string) => string; onDownload: () => void }) {
  return (
    <div className="text-center max-w-xs px-6">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-lg bg-card shadow-sm border border-border">
        <FileText className="h-10 w-10 text-muted-foreground/60" />
      </div>
      <p className="mt-4 text-sm font-semibold text-foreground">{file.filename}</p>
      <p className="mt-1 text-xs text-muted-foreground">This file type can't be previewed in the app. Open or download it to view.</p>
      <button
        onClick={onDownload}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[var(--v4-accent)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
      >
        <Eye className="h-4 w-4" /> {t('open')}
      </button>
    </div>
  );
}

function guessMimeFromName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', heic: 'image/heic',
    mp3: 'audio/mpeg', m4a: 'audio/mp4', webm: 'audio/webm',
    wav: 'audio/wav', ogg: 'audio/ogg',
    mp4: 'video/mp4', mov: 'video/quicktime',
  };
  return map[ext] || '';
}

/** Inline grid cell on the Send hero - one of 4 action tiles. */
function SendAction({ onClick, icon: Icon, label }: { onClick: () => void; icon: any; label: string }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-md border border-border bg-card p-3 transition-all hover:border-[var(--v4-accent)] hover:bg-[var(--v4-accent-tint)] hover:-translate-y-0.5 active:scale-[0.97]"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[var(--v4-accent)] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-[11px] font-semibold text-foreground leading-tight text-center">{label}</span>
    </button>
  );
}

/**
 * SendSheet - the rich "Send anything to your accountant" composer.
 * Four modes: camera · upload · voice · text. Mocked for the prototype.
 */
function AddFileSheet({ onClose, t }: { onClose: () => void; t: (k: string) => string }) {
  const [mode, setMode] = useState<'menu' | 'message' | 'voice' | 'sent'>('menu');
  const [msg, setMsg] = useState('');
  const [recordSec, setRecordSec] = useState(0);

  // Voice timer
  useEffect(() => {
    if (mode !== 'voice') return;
    const id = setInterval(() => setRecordSec(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [mode]);

  const send = () => { setMode('sent'); setTimeout(onClose, 1500); };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[720px] rounded-t-2xl border-t border-border bg-card p-5 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-muted" />

        {/* Menu */}
        {mode === 'menu' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold">{t('snap_receipt')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{t('snap_hint')}</p>

            <div className="grid grid-cols-2 gap-2">
              <SheetOption icon={Camera}             title={t('opt_camera')}  hint={t('opt_camera_hint')}  onClick={send} />
              <SheetOption icon={Upload}             title={t('opt_upload')}  hint={t('opt_upload_hint')}  onClick={send} />
              <SheetOption icon={Mic}                title={t('opt_voice')}   hint={t('opt_voice_hint')}   onClick={() => setMode('voice')} />
              <SheetOption icon={MessageSquareText}  title={t('opt_message')} hint={t('opt_message_hint')} onClick={() => setMode('message')} />
            </div>

            {/* Tertiary channels - kept available but secondary */}
            <div className="mt-5 border-t border-border/60 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t('more_channels')}</p>
              <div className="space-y-1.5 text-[11px] text-muted-foreground">
                <p>📧 <span className="text-foreground">james-tiling-1234@receipts.ronidoc.com</span></p>
                <p>💬 WhatsApp <span className="text-foreground">+61 4XX XXX XXX</span></p>
              </div>
            </div>
          </>
        )}

        {/* Type a message */}
        {mode === 'message' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold">{t('opt_message')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <textarea
              autoFocus
              value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder={t('type_your_message')}
              rows={5}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none resize-none"
            />
            <button
              onClick={send}
              disabled={!msg.trim()}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-3 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)] disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> {t('send_now')}
            </button>
          </>
        )}

        {/* Record voice */}
        {mode === 'voice' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold">{t('opt_voice')}</h2>
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-5">{t('opt_voice_hint')}</p>

            <div className="flex flex-col items-center py-6">
              <div className="relative flex h-24 w-24 items-center justify-center">
                <div className="absolute inset-0 animate-ping rounded-full bg-[var(--v4-accent)] opacity-20" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white shadow-lg">
                  <Mic className="h-8 w-8" />
                </div>
              </div>
              <p className="mt-5 text-2xl font-bold tabular-nums">{String(Math.floor(recordSec / 60)).padStart(2, '0')}:{String(recordSec % 60).padStart(2, '0')}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-[var(--v4-accent-text)] font-semibold">{t('recording')}</p>
            </div>
            <button
              onClick={send}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-3 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
            >
              <Send className="h-4 w-4" /> {t('send_now')}
            </button>
          </>
        )}

        {/* Sent confirmation */}
        {mode === 'sent' && (
          <div className="flex flex-col items-center py-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-[var(--v4-accent)]" strokeWidth={2.5} />
            <p className="mt-3 text-base font-bold">{t('message_sent')}</p>
          </div>
        )}
      </div>
    </>
  );
}

function SheetOption({ icon: Icon, title, hint, onClick }: { icon: any; title: string; hint: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-start gap-2 rounded-md border border-border bg-card p-3 text-start hover:border-[var(--v4-accent)] hover:bg-[var(--v4-accent-tint)] transition-colors"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--v4-accent)] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span>
        <p className="text-sm font-semibold text-foreground leading-tight">{title}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{hint}</p>
      </span>
    </button>
  );
}

export { FILES };

function InsightKpi({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border bg-background p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase font-medium tracking-wider text-muted-foreground">
        <Icon className="w-3 h-3" />
        <span className="truncate">{label}</span>
      </div>
      <div className="text-base font-semibold mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
